import { apiClient } from './apiClient';
import { parseWorkflowHistoryDates } from './approvalsApi';
import { buildReportQueryString, fetchAllPages, REPORT_EXPORT_PAGE_SIZE } from './reportPaging';
import type { TimesheetWorkflowHistory } from '@/types/workflow-status';

export interface AuditTrailRowDto {
  timesheetId: number;
  employeeId: number;
  employeeName?: string | null;
  department?: string | null;
  weekEnding: string;
  status: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  recalledAt?: string | null;
  wasRecalled: boolean;
  totalHours: number;
  workflowInstanceId?: number | null;
  workflowName?: string | null;
  currentStageName?: string | null;
  workflowStatus?: string | null;
  lastEventType?: string | null;
  lastEventActorName?: string | null;
  lastEventAt?: string | null;
  hasApproverEdits: boolean;
}

export interface PagedAuditTrailResponse {
  items: AuditTrailRowDto[];
  /** Rows matching every filter; drives paging. */
  totalCount: number;
  /** Summary counts: every timesheet in scope (including drafts), ignoring the status filter. */
  totalTimesheetsCount: number;
  approvedCount: number;
  rejectedCount: number;
  submittedCount: number;
  recalledCount: number;
  draftCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface AuditTrailQueryParams {
  employeeIds?: number[];
  projectIds?: number[];
  departments?: string[];
  statuses?: string[];
  billableOnly?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function buildAuditTrailQueryString(params: AuditTrailQueryParams): string {
  return buildReportQueryString({
    employeeIds: params.employeeIds,
    projectIds: params.projectIds,
    departments: params.departments,
    statuses: params.statuses,
    billableOnly: params.billableOnly ? true : undefined,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    search: params.search?.trim(),
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
  });
}

export const auditTrailApi = {
  async getAuditTrail(params: AuditTrailQueryParams = {}): Promise<PagedAuditTrailResponse> {
    return apiClient.get<PagedAuditTrailResponse>(`/reports/audit-trail${buildAuditTrailQueryString(params)}`);
  },

  /** Every row matching the filters, fetched page by page at the server's maximum page size. */
  async getAllAuditTrailRows(
    params: Omit<AuditTrailQueryParams, 'pageNumber' | 'pageSize'> = {},
  ): Promise<AuditTrailRowDto[]> {
    return fetchAllPages(
      (pageNumber, pageSize) => auditTrailApi.getAuditTrail({ ...params, pageNumber, pageSize }),
      REPORT_EXPORT_PAGE_SIZE,
    );
  },

  async getHistory(timesheetId: number): Promise<TimesheetWorkflowHistory> {
    const history = await apiClient.get<unknown>(`/reports/audit-trail/${timesheetId}/history`);
    return parseWorkflowHistoryDates(history);
  },
};
